import {
  AdsClick,
  Bedtime,
  Chat,
  Coffee,
  Commute,
  ConnectedTv,
  ContactMail,
  Dashboard,
  DepartureBoard,
  DirectionsBus,
  DirectionsCar,
  DirectionsRailway,
  DirectionsRun,
  DirectionsWalk,
  Facebook,
  FactCheck,
  Google,
  Group,
  Groups,
  Hotel,
  Interests,
  Mail,
  Map,
  Moving,
  Newspaper,
  NoTransfer,
  Numbers,
  People,
  Percent,
  Person,
  Place,
  Public,
  Restaurant,
  School,
  ShoppingCart,
  Sort,
  Train,
  ViewArray,
  VolunteerActivism,
  WbTwilight,
  X,
} from "@mui/icons-material";
import { EyeotaLogo, MetaLogo } from "@/assets/partners";

export const audienceSortOptions = [
  { name: "default", label: "Created Date" },
  { name: "name", label: "Audience Name" },
];

export const audienceFilterOptions = [
  { name: "build", label: "Build" },
  { name: "upload", label: "Upload" },
];

export const defaultSegment = { segment_id: 0, segment_name: "" };

export const operators = ["AND", "OR", "NOT"];
export const groupOperators = ["AND", "OR"];
export const buildModes = ["broad", "balanced", "narrow"];
export const modeThreshold: { [key: string]: number } = {
  broad: 4,
  balanced: 6,
  narrow: 8,
};
export const scoreTypeMap: { [name: string]: string } = {
  score_wt: "Index",
  rate: "Penetration",
};

export const demographics = ["Age Band", "Income Band", "Lifestage"];

export const uploadModeOptions = [
  {
    value: "geo",
    label: "Geo",
    description:
      "Upload a file containing postcodes for pure geo-based insight and activation",
    icon: <Place />,
    disabled: false,
  },
  {
    value: "customer",
    label: "Customer",
    description:
      "Upload your customer postcodes to understand weighted insights and enrich your customers with attributes",
    icon: <People />,
    disabled: true,
  },
  {
    value: "travletime",
    label: "Travel-Time",
    description:
      "Upload your site locations, define a travel time, and get insights about the postcodes surrounding your sites",
    icon: <DepartureBoard />,
    disabled: true,
  },
];

export const insightScoreOptions = [
  {
    value: "score_wt",
    label: "Index",
    icon: <Numbers />,
    description:
      "Index: The penetration value compared against the national average",
  },
  {
    value: "rate",
    label: "Penetration",
    icon: <Percent />,
    description:
      "Penetration: An estimate of the % of people we expect to have each variable",
  },
  // {
  //   value: "score_wt",
  //   label: "Weighted Index",
  //   icon: <Scale />
  // },
  {
    value: "score_rgn",
    label: "Region Index",
    icon: <Public />,
    description:
      "Regional Index: The penetration value compared against a regional average",
  },
];

export const insightLevelOptions = [
  {
    value: 3,
    label: "Local",
    description:
      "Local: These brands have a really small reach that tends not to extend beyond the local area",
    short: "Loc.",
    icon: <Person />,
  },
  {
    value: 2,
    label: "Regional",
    description:
      "Regional: These brands may be well known, but due to the nature of their business tend to still only attract customers who live nearby",
    short: "Reg.",
    icon: <Group />,
  },
  {
    value: 1,
    label: "National",
    description:
      "National: These will be the most well known and popular brands that have a nationwide presence",
    short: "Nat.",
    icon: <Groups />,
  },
];

export const insightSortOptions = [
  {
    value: "asc",
    label: "Asc.",
    icon: <Sort sx={{ transform: "scaleY(-1)" }} />,
    description: "Sort the data in ascending order based on the selected score",
  },
  {
    value: "desc",
    label: "Desc.",
    icon: <Sort />,
    description:
      "Sort the data in descending order based on the selected score",
  },
];

export const insightTabOptions = [
  {
    name: "pen_portrait",
    label: "Pen Portrait",
    icon: <Dashboard fontSize="small" />,
    permission: "",
    description:
      "A summary of your audience to give you a view of all of their behaviours in one place",
    showScores: true,
    showSorts: false,
    showLevels: true,
    showSearch: false,
  },
  {
    name: "map",
    label: "Map",
    icon: <Map fontSize="small" />,
    permission: "",
    description:
      "Visualise where your audience live and which geos are the best places to reach them",
    showScores: false,
    showLevels: false,
    showSorts: false,
    showSearch: false,
  },
  {
    name: "demographics",
    label: "Demographics",
    icon: <People fontSize="small" />,
    permission: "",
    description: "Understand the demographic makeup of your audience",
    showScores: true,
    showLevels: false,
    showSorts: false,
    showSearch: false,
  },
  {
    name: "interests",
    label: "Interests",
    icon: <Interests fontSize="small" />,
    permission: "",
    description:
      "Analyse the interests of your audience to see what they love and care about",
    showScores: true,
    showLevels: false,
    showSorts: false,
    showSearch: false,
  },
  {
    name: "spend",
    label: "Spend Patterns",
    icon: <ShoppingCart fontSize="small" />,
    permission: "data:snoop",
    description:
      "Understand where your audience is likely to spend their money",
    showScores: true,
    showLevels: true,
    showSorts: false,
    showSearch: true,
  },
  {
    name: "causes",
    label: "Causes",
    icon: <VolunteerActivism fontSize="small" />,
    permission: "",
    description:
      "See which charities and causes your audience are most likely to support",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
  },
  {
    name: "media",
    label: "Media",
    icon: <Newspaper fontSize="small" />,
    permission: "data:tgi",
    description:
      "See which media channels your audience is most likely to resonate with",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
  },
  {
    name: "attitudes",
    label: "Attitudes",
    icon: <FactCheck fontSize="small" />,
    permission: "data:tgi",
    description:
      "Analyse the behaviour of your audience along with their attitudes and preferences",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
  },
  {
    name: "digital",
    label: "Digital Consumption",
    icon: <ConnectedTv fontSize="small" />,
    permission: "data:tgi",
    description:
      "Understand how your audience use the internet and other digital products",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
  },
  {
    name: "movement",
    label: "Movement",
    icon: <DirectionsRun fontSize="small" />,
    permission: "data:o2",
    description:
      "Show when and how your audience move around",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
  },
  {
    name: "top scoring",
    label: "Top Scoring",
    icon: <Moving fontSize="small" />,
    permission: "",
    description: "",
    showScores: true,
    showSorts: true,
    showLevels: false,
    showSearch: false,
  },
];

export const exportOptions = [
  {
    name: "postcode",
    label: "Postcode",
    type: "Geo",
    description: "The best Postcodes to reach your audience",
    icon: <Place />,
    allow_variables: true,
  },
  {
    name: "sector",
    label: "Sector",
    type: "Geo",
    description: "The best Postal Sectors to reach your audience",
    icon: <Place />,
    allow_variables: true,
  },
  {
    name: "door_drop",
    label: "Door Drop",
    type: "Mail",
    description: "Optimise your Door Drop campaigns using Postal Sectors",
    icon: <Mail />,
    allow_variables: false,
  },
  {
    name: "partially_addressed",
    label: "Partially Addressed",
    type: "Mail",
    description: "Target the right Postcodes for Partially Addressed Mail ",
    icon: <ContactMail />,
    allow_variables: false,
  },
  {
    name: "ooh",
    label: "OOH",
    type: "OOH",
    description: "Integrate with (D)OOH using Postcode Sectors",
    icon: <ViewArray />,
    allow_variables: false,
  },
  {
    name: "addressable_tv",
    label: "Addressable TV",
    type: "TV",
    description:
      "Reach the right households for your Addressable TV campaigns using Postal Sectors",
    icon: <ConnectedTv />,
    allow_variables: false,
  },
  {
    name: "geo_onboarding",
    label: "Geo Onboarding",
    type: "Digital",
    description:
      "Upload Postcodes into Digital Onboarding Environments (e.g. LiveRamp or Amazon Ads)",
    icon: <AdsClick />,
    allow_variables: false,
  },
  {
    name: "dv360",
    label: "DV360",
    type: "Digital",
    description: "Upload Latitude and Longitude pairs into DV360",
    icon: <Google />,
    allow_variables: false,
  },
  {
    name: "meta",
    label: "Meta",
    type: "Social",
    description: "Add granularity to your campaign in Meta Ads Manager",
    icon: <Facebook />,
    allow_variables: false,
  },
  {
    name: "twitter",
    label: "Twitter",
    type: "Social",
    description: "Optimise your Social campaign on X using Postal Sectors",
    icon: <X />,
    allow_variables: false,
  },
  {
    name: "lookalike",
    label: "Lookalikes",
    type: "Geo",
    description: "Create lookalikes from your Upload Audience to find postcodes that are similar",
    icon: <People />,
    allow_variables: false,
  },
];

export const campaignTypeOptions = [
  {
    value: "eyeota",
    label: "Programmatic",
    description:
      "Activate your prebuilt Starcount audiences within your favourite programmatic buying platforms",
    icon: <AdsClick />,
    disabled: false,
    message:
      `
      Proceeding will mean that your activation will get submitted to Eyeota and begin populating
      Please allow up to 7 days for it to be fully populated
      `
  },
  {
    value: "meta",
    label: "Social",
    description:
      "Create recipes from your audiences using the established Meta platform taxonomy for social activations",
    icon: <Chat />,
    disabled: false,
    message:
      `
      Proceeding will mean that your activation will get submitted to Starcount and your Meta recipe will be created.
      It will then be your responsibility to copy this recipe into AdsManager to activate your audience.
     `
  },
];

export const toDemogStart = (v: string) => {
  switch (v) {
    case "Age Band":
      return "18-24";
    case "Income Band":
      return "£20k";
    case "Lifestage":
      return "Young";
    case "Social Grade":
      return "DE";
    case "House Price":
      return "£150k";
    case "Socio Economic Levels":
      return "10";
    case "Monthly Expenditure":
      return "£50";
    default:
      return "";
  }
};

export const toDemogEnd = (v: string) => {
  switch (v) {
    case "Age Band":
      return "65+";
    case "Income Band":
      return "£100k+";
    case "Lifestage":
      return "Retired";
    case "Social Grade":
      return "AB";
    case "House Price":
      return "£800k+";
    case "Socio Economic Levels":
      return "1";
    case "Monthly Expenditure":
      return "£500+";
    default:
      return "";
  }
};

export const activationPlatforms = {
  eyeota: [
    "Adform",
    "Adobe Ad Cloud (AAC)",
    "Adobe Audience Manager (AAM)",
    "Amazon (upon request)",
    "Aroscop (upon request)",
    "Basis",
    "BidTheatre",
    "Brand Network (Meta/Instagram, X, Pinterest & TikTok)",
    "DV360",
    "Epsilon Digital",
    "Equativ",
    "Google",
    "InMobi",
    "KARGO",
    "Knorex",
    "Lotame",
    "Magnite DV+",
    "MediaIQ (upon request)",
    "MGID (upon request)",
    "Neustar",
    "Nexxen (DSP & SSP)",
    "Outbrain SSP & Zemanta DSP",
    "PubMatic",
    "Roku OneView",
    "Sabio (upon request)",
    "SmartyAds",
    "StackAdapt",
    "Taboola",
    "The Trade Desk (TTD)",
    "Vistar Media",
    "Xandr (Apnnexus) & Curate (SSP)",
    "Yahoo!",
    "YOC",
    "Zeta (Sizmek)",
  ],
  meta: ["Meta"],
};

export const activationLogos = {
  eyeota: <EyeotaLogo />,
  meta: <MetaLogo />,
};

export const activationStatusDescription: { [key: string]: string } = {
  submitted: "The activation has been submitted to the Starcount team",
  "in process": "The data has been created and sent to Eyeota for processing",
  ready:
    "The activation segments have been created and are ready to be populated in the platform",
  populated:
    "The segments have been fully populated in the platform and are ready to buy against",
  complete: "The campaign dates have passed and the activation has expired",
};


export const movementOptions = [
  {
    name: 'Time of Day',
    options: [
      {name: 'Early Morning', label: 'Early Morning (5am - 7am)', icon: <WbTwilight />},
      {name: 'Morning Commute', label: 'Morning Commute (7am - 10am)', icon: <Train />},
      {name: 'Late Morning', label: 'Late Morning (10am - 12pm)', icon: <Coffee />},
      {name: 'Lunchtime', label: 'Lunchtime (12pm - 2pm)', icon: <Restaurant />},
      {name: 'Afternoon', label: 'Afternoon (2pm - 5pm)', icon: <School />},
      {name: 'Evening Commute', label: 'Evening Commute (5pm - 7pm)', icon: <Commute />},
      {name: 'Late Night', label: 'Late Night (7pm - 12am)', icon: <Hotel />},
      {name: 'Night Owls', label: 'Night Owls (12am - 5am)', icon: <Bedtime />},
    ]
  },
  {
    name: 'Transport Mode',
    options: [
      {name: 'Walking or Cycling', label: 'Walking or Cycling', icon: <DirectionsWalk />},
      {name: 'Rail', label: 'Rail', icon: <DirectionsRailway />},
      {name: 'Driving', label: 'Driving', icon: <DirectionsCar />},
    ]
  },
  {
    name: 'Commuters',
    options: [
      {name: 'Commuters', label: 'Commuters', icon: <DirectionsBus />},
      {name: 'Non-Commuters', label: 'Non-Commuters', icon: <NoTransfer />},
    ]
  },
]

export const genderOptions = [
  {name: 'male', label: 'Male'},
  {name: 'female', label: 'Female'},
  {name: 'both', label: 'Both'}
]

export const countryOptions = [
  {'name': 'GB', label: 'United Kingdom'},
  {'name': 'US', label: 'United States'}
]
